import WebViewer from "@pdftron/webviewer";
import { useQuery } from "@tanstack/react-query";
import { FileText, X } from "lucide-react";
import {
  type ForwardedRef,
  type RefObject,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { TemplateFieldsPanelContent } from "@/components/features/settings/defaults/matter-templates/TemplateFieldsDialog";
import { useTheme } from "@/components/ThemeProvider";
import { isDarkTheme } from "@/lib/themes";
import { Button, LoadingSpinner, Typography } from "@/components/ui";
import { generateS3ItemPreSignedUrlV1 } from "@/lib/data/requests/s3";
import { queryKeys } from "@/lib/data/static";
import { cn } from "@/lib/utils/cn";
import { getFileNameParts } from "@/lib/utils/matterFile";

export const DOCX_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const DEFAULT_DOCX_EDITOR_ZOOM = 0.9;
const TEMPLATE_FIELDS_PANEL_WIDTH_CLASS = "w-[420px]";

export type DocxEditorHandle = {
  exportDocument: () => Promise<Blob | null>;
  insertTextAtCursor?: (text: string) => Promise<boolean>;
  setPrettyTagsEnabled?: (enabled: boolean) => Promise<void>;
  isReady: boolean;
  isLoading: boolean;
};

export type DocxEditorFileType =
  | "matter-file"
  | "firm-file"
  | "matter-template"
  | "matter-signature-request-file"
  | "matter-share-folder-file";

const findElementInOpenShadowRoots = (
  root: ParentNode,
  selector: string,
): HTMLElement | null => {
  const directMatch =
    "querySelector" in root ? root.querySelector(selector) : null;
  if (directMatch instanceof HTMLElement) {
    return directMatch;
  }

  if (!("querySelectorAll" in root)) {
    return null;
  }

  const elements = Array.from(root.querySelectorAll("*"));
  for (const element of elements) {
    if (element instanceof Element && element.shadowRoot) {
      const nestedMatch = findElementInOpenShadowRoots(
        element.shadowRoot,
        selector,
      );
      if (nestedMatch) {
        return nestedMatch;
      }
    }
  }

  return null;
};

type DocxEditorProps = {
  fileName: string;
  presignedUrlKey?: string;
  fileType: DocxEditorFileType;
  initialDocUrl?: string;
  loadBlankDocument?: boolean;
  licenseKey?: string;
  canSave?: boolean;
  onSave?: (payload: { blob: Blob; fileName: string }) => Promise<void> | void;
  showSaveButton?: boolean;
  showHeader?: boolean;
  headerActions?: React.ReactNode;
  onTemplateFieldsRequest?: () => void;
  onReadyChange?: (ready: boolean) => void;
  onScrollContainerReady?: (container: HTMLDivElement | null) => void;
  isActive?: boolean;
  className?: string;
};

export const DocxEditor = forwardRef(function DocxEditor(
  {
    fileName,
    presignedUrlKey,
    fileType,
    initialDocUrl,
    loadBlankDocument = false,
    licenseKey,
    canSave = true,
    onSave,
    showSaveButton = true,
    showHeader = true,
    headerActions,
    onReadyChange,
    onScrollContainerReady,
    isActive = true,
    className,
  }: DocxEditorProps,
  ref: ForwardedRef<DocxEditorHandle>,
) {
  const viewerRef = useRef<HTMLDivElement>(null);
  const viewerInstanceRef = useRef<Awaited<
    ReturnType<typeof WebViewer>
  > | null>(null);
  const disposePromiseRef = useRef<Promise<void> | null>(null);
  const isInitializingRef = useRef(false);
  const resizeTimeoutsRef = useRef<number[]>([]);
  const templateFieldButtonTimeoutsRef = useRef<number[]>([]);
  const templateFieldsToolbarObserverRef = useRef<MutationObserver | null>(
    null,
  );
  const canceledRef = useRef<boolean>(false);
  const prettyTagsEnabledRef = useRef(false);
  const prettyTagsRefreshTimeoutRef = useRef<number | null>(null);
  const pagesUpdatedHandlerRef = useRef<
    ((changes?: { contentChanged?: number[] }) => void) | null
  >(null);
  const { theme } = useTheme();
  const [isReady, setIsReady] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isInitializingViewer, setIsInitializingViewer] = useState(false);
  const [documentError, setDocumentError] = useState<string | null>(null);
  const [isTemplateFieldsPanelOpen, setIsTemplateFieldsPanelOpen] =
    useState(false);

  const {
    data: presignedUrl,
    isLoading: isLoadingFileUrl,
    isError: isErrorFileUrl,
  } = useQuery({
    queryKey: [queryKeys.matterFileImages, presignedUrlKey, "docx-editor"],
    enabled: Boolean(presignedUrlKey) && !initialDocUrl,
    refetchOnMount: "always",
    staleTime: 0,
    gcTime: 0,
    queryFn: async () => {
      if (!presignedUrlKey) {
        return "";
      }
      const response = await generateS3ItemPreSignedUrlV1(fileType)({
        clientMethod: "get_object",
        key: presignedUrlKey,
      });
      return response.data.presignedUrl;
    },
  });

  const resolvedDocUrl = initialDocUrl ?? presignedUrl ?? "";
  const shouldLoadBlankDocument = Boolean(loadBlankDocument);
  const hasDocumentSource = Boolean(resolvedDocUrl) || shouldLoadBlankDocument;
  const resolvedFileName = useMemo(() => {
    if (!fileName) return "document.docx";
    const { fileExtension } = getFileNameParts(fileName);
    if (!fileExtension) {
      return `${fileName}.docx`;
    }
    return fileName;
  }, [fileName]);
  const resolvedTheme = isDarkTheme(theme) ? "dark" : "light";

  const setPrettyTagsEnabled = useCallback(async (enabled: boolean) => {
    prettyTagsEnabledRef.current = enabled;
    const instance = viewerInstanceRef.current;
    if (!instance) return;
    const documentViewer = instance.Core.documentViewer;
    if (!enabled) {
      documentViewer.clearSearchResults();
      return;
    }
    try {
      const modeOptions = [
        instance.Core.Search.Mode.REGEX,
        instance.Core.Search.Mode.HIGHLIGHT,
      ];
      const searchStream = documentViewer.search(
        String.raw`\{\{[^}]+\}\}`,
        modeOptions,
      );
      const results = await (
        searchStream as unknown as { getAll: () => Promise<object[]> }
      ).getAll();
      documentViewer.clearSearchResults();
      if (results.length > 0) {
        documentViewer.displayAdditionalSearchResults(results);
        documentViewer.setActiveSearchResult(results[0]);
      }
    } catch (error) {
      console.error("Failed to highlight template tags", error);
    }
  }, []);

  const schedulePrettyTagsRefresh = useCallback(() => {
    if (!prettyTagsEnabledRef.current) return;
    if (prettyTagsRefreshTimeoutRef.current) {
      window.clearTimeout(prettyTagsRefreshTimeoutRef.current);
    }
    prettyTagsRefreshTimeoutRef.current = window.setTimeout(() => {
      void setPrettyTagsEnabled(true);
    }, 400);
  }, [setPrettyTagsEnabled]);

  const insertTextAtCursor = useCallback(
    async (text: string) => {
      const instance = viewerInstanceRef.current;
      if (!instance) return false;
      const officeEditor = instance.Core.documentViewer
        .getDocument()
        .getOfficeEditor();
      try {
        await navigator.clipboard.writeText(text);
      } catch {
        return false;
      }
      try {
        officeEditor.focusContent();
        await officeEditor.pasteText(false);
        if (prettyTagsEnabledRef.current) {
          schedulePrettyTagsRefresh();
        }
        return true;
      } catch {
        return false;
      }
    },
    [schedulePrettyTagsRefresh],
  );

  const applyViewerUISettings = useCallback(
    (instance: Awaited<ReturnType<typeof WebViewer>>) => {
      const customCss = `
        [data-element="officeEditorFileName"] { display: none !important; }
        [data-element="settingsThemeSection"] { display: none !important; }
        [data-element="trackChangeDropdown"] { display: none !important; }
        [data-element="trackChangeOverlayButton"] { display: none !important; }
        .theme-options { display: none !important; }
      `;

      instance.UI.disableElements([
        "officeEditorFileName",
        "settingsThemeSection",
        "themeButton",
        "toggleThemeButton",
        "newDocumentButton",
        "saveAsButton",
        "trackChangeDropdown",
        "trackChangeOverlayButton",
        "toolbarGroup-FillAndSign",
      ]);
      if (!viewerRef.current) return;
      const existingStyle = viewerRef.current.querySelector(
        'style[data-docx-ui="true"]',
      );
      if (existingStyle) {
        existingStyle.textContent = customCss;
        return;
      }
      const style = document.createElement("style");
      style.setAttribute("data-docx-ui", "true");
      style.textContent = customCss;
      viewerRef.current.appendChild(style);
    },
    [],
  );

  const applyDefaultZoom = useCallback(
    (instance: Awaited<ReturnType<typeof WebViewer>>) => {
      instance.UI.setZoomLevel(DEFAULT_DOCX_EDITOR_ZOOM);
    },
    [],
  );

  const applyTemplateFieldsDomFallback = useCallback(() => {
    const templateFieldsButtonDataElement = "lawworks-template-vars-button";
    const insertGroupedItems =
      (viewerRef.current
        ? findElementInOpenShadowRoots(
            viewerRef.current,
            '[data-element="officeEditorInsertGroupedItems"]',
          )
        : null) ??
      findElementInOpenShadowRoots(
        document,
        '[data-element="officeEditorInsertGroupedItems"]',
      );

    if (!insertGroupedItems) return;

    const existingDomButton = findElementInOpenShadowRoots(
      insertGroupedItems,
      `[data-element="${templateFieldsButtonDataElement}"]`,
    );
    if (existingDomButton) return;

    const button = document.createElement("button");
    button.type = "button";
    button.setAttribute("data-element", templateFieldsButtonDataElement);
    button.className = "Button ActionButton button-text-icon modular-ui";
    button.textContent = "Template Vars";
    button.title = "Insert template variables";
    button.style.minWidth = "fit-content";
    button.style.paddingInline = "12px";
    button.addEventListener("click", () => {
      setIsTemplateFieldsPanelOpen(true);
    });

    const filePickerHandler = Array.from(insertGroupedItems.children).find(
      (child) =>
        child instanceof HTMLElement &&
        child.classList.contains("FilePickerHandler"),
    );

    insertGroupedItems.insertBefore(button, filePickerHandler ?? null);
  }, []);

  const attachTemplateFieldsInsertTabListeners = useCallback(() => {
    const attachListener = (selector: string) => {
      const insertTabButton = findElementInOpenShadowRoots(document, selector);
      if (!insertTabButton) return;
      if (
        insertTabButton.getAttribute("data-lawworks-template-vars-listener") ===
        "true"
      ) {
        return;
      }

      insertTabButton.setAttribute(
        "data-lawworks-template-vars-listener",
        "true",
      );
      insertTabButton.addEventListener("click", () => {
        [50, 200, 500].forEach((delayMs) => {
          window.setTimeout(() => {
            if (canceledRef.current) return;
            applyTemplateFieldsDomFallback();
          }, delayMs);
        });
      });
    };

    attachListener('[data-element="toolbarGroup-Insert"]');
    attachListener('[data-element="dropdown-item-toolbarGroup-Insert"]');
  }, [applyTemplateFieldsDomFallback]);

  const observeTemplateFieldsToolbar = useCallback(() => {
    templateFieldsToolbarObserverRef.current?.disconnect();

    const viewerElement = viewerRef.current;
    if (!viewerElement) return;

    const observedRoots = new WeakSet<Node>();
    const observer = new MutationObserver(() => {
      observeOpenShadowRoots(viewerElement);
      applyTemplateFieldsDomFallback();
    });

    const observeOpenShadowRoots = (root: ParentNode) => {
      if (observedRoots.has(root)) return;
      observedRoots.add(root);
      observer.observe(root, { childList: true, subtree: true });

      if (!("querySelectorAll" in root)) return;
      root.querySelectorAll("*").forEach((element) => {
        if (element.shadowRoot) {
          observeOpenShadowRoots(element.shadowRoot);
        }
      });
    };

    observeOpenShadowRoots(viewerElement);
    templateFieldsToolbarObserverRef.current = observer;
  }, [applyTemplateFieldsDomFallback]);

  const applyTemplateFieldsRibbonButton = useCallback(
    (instance: Awaited<ReturnType<typeof WebViewer>>) => {
      const templateFieldsButtonDataElement = "lawworks-template-vars-button";
      try {
        instance.UI.setHeaderItems((header) => {
          const tryAttachToHeaderGroup = (headerGroup: string) => {
            let insertHeader: {
              getItems: () => unknown[];
              push: (item: unknown) => void;
            };
            try {
              insertHeader = header.getHeader(headerGroup);
            } catch {
              return false;
            }
            const existingItems = insertHeader.getItems();
            if (
              existingItems.some(
                (item) =>
                  (item as { dataElement?: string }).dataElement ===
                  templateFieldsButtonDataElement,
              )
            ) {
              return true;
            }

            insertHeader.push({
              type: "customElement",
              dataElement: templateFieldsButtonDataElement,
              render: () => {
                const button = document.createElement("button");
                button.type = "button";
                button.textContent = "Template Vars";
                button.onclick = () => {
                  setIsTemplateFieldsPanelOpen(true);
                };
                button.style.height = "32px";
                button.style.padding = "0 12px";
                button.style.border = "1px solid var(--border)";
                button.style.borderRadius = "6px";
                button.style.background = "var(--background)";
                button.style.color = "var(--foreground)";
                button.style.fontSize = "12px";
                button.style.fontWeight = "600";
                button.style.cursor = "pointer";
                button.style.whiteSpace = "nowrap";
                return button;
              },
            });

            return true;
          };

          tryAttachToHeaderGroup("toolbarGroup-Insert");
        });
      } catch (error) {
        console.warn("Failed to inject Apryse ribbon button via UI API", error);
      }
      applyTemplateFieldsDomFallback();
      attachTemplateFieldsInsertTabListeners();
      observeTemplateFieldsToolbar();
    },
    [
      applyTemplateFieldsDomFallback,
      attachTemplateFieldsInsertTabListeners,
      observeTemplateFieldsToolbar,
    ],
  );

  const scheduleTemplateFieldsRibbonButton = useCallback(
    (instance: Awaited<ReturnType<typeof WebViewer>>) => {
      templateFieldButtonTimeoutsRef.current.forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
      templateFieldButtonTimeoutsRef.current = [];

      [0, 150, 400, 900].forEach((delayMs) => {
        templateFieldButtonTimeoutsRef.current.push(
          window.setTimeout(() => {
            if (canceledRef.current || viewerInstanceRef.current !== instance) {
              return;
            }
            applyTemplateFieldsRibbonButton(instance);
          }, delayMs),
        );
      });
    },
    [applyTemplateFieldsRibbonButton],
  );

  const disposeViewer = useCallback(async () => {
    if (disposePromiseRef.current) {
      await disposePromiseRef.current;
    }
    const instance = viewerInstanceRef.current;
    if (!instance) return;
    const documentViewer = instance.Core.documentViewer;
    if (pagesUpdatedHandlerRef.current) {
      documentViewer.removeEventListener(
        "pagesUpdated",
        pagesUpdatedHandlerRef.current,
      );
      pagesUpdatedHandlerRef.current = null;
    }
    if (prettyTagsRefreshTimeoutRef.current) {
      window.clearTimeout(prettyTagsRefreshTimeoutRef.current);
      prettyTagsRefreshTimeoutRef.current = null;
    }
    templateFieldButtonTimeoutsRef.current.forEach((timeoutId) => {
      window.clearTimeout(timeoutId);
    });
    templateFieldButtonTimeoutsRef.current = [];
    templateFieldsToolbarObserverRef.current?.disconnect();
    templateFieldsToolbarObserverRef.current = null;
    const disposePromise = instance.UI.dispose();
    disposePromiseRef.current = disposePromise;
    viewerInstanceRef.current = null;
    try {
      await disposePromise;
    } finally {
      if (disposePromiseRef.current === disposePromise) {
        disposePromiseRef.current = null;
      }
    }
  }, []);

  const scheduleViewerResize = useCallback(() => {
    const instance = viewerInstanceRef.current;
    if (!instance) return;
    const ui = instance.UI as unknown as { resize?: () => void };
    resizeTimeoutsRef.current.forEach((timeoutId) => {
      window.clearTimeout(timeoutId);
    });
    resizeTimeoutsRef.current = [];
    requestAnimationFrame(() => {
      ui.resize?.();
    });
    resizeTimeoutsRef.current.push(
      window.setTimeout(() => {
        ui.resize?.();
      }, 150),
    );
    resizeTimeoutsRef.current.push(
      window.setTimeout(() => {
        ui.resize?.();
      }, 450),
    );
  }, []);

  useEffect(() => {
    if (!onScrollContainerReady) return;
    const viewerElement = viewerRef.current;
    if (!viewerElement || !isReady) {
      onScrollContainerReady(null);
      return;
    }

    const getScrollContainer = () =>
      findElementInOpenShadowRoots(
        viewerElement,
        '[data-element="documentContainer"], .DocumentContainer',
      ) as HTMLDivElement | null;

    const emitScrollContainer = () => {
      onScrollContainerReady(getScrollContainer());
    };

    emitScrollContainer();

    const observer = new MutationObserver(() => {
      emitScrollContainer();
    });

    observer.observe(viewerElement, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
      onScrollContainerReady(null);
    };
  }, [isReady, onScrollContainerReady]);

  useEffect(() => {
    const viewerElement = viewerRef.current;
    if (!viewerElement || !isReady) return;

    const getScrollContainer = () =>
      findElementInOpenShadowRoots(
        viewerElement,
        '[data-element="documentContainer"], .DocumentContainer',
      ) as HTMLDivElement | null;

    const handleWheel = (event: WheelEvent) => {
      const scrollContainer = getScrollContainer();
      if (!scrollContainer) return;

      const activeElement = document.activeElement;
      if (
        activeElement instanceof HTMLElement &&
        (activeElement.tagName === "INPUT" ||
          activeElement.tagName === "TEXTAREA" ||
          activeElement.isContentEditable)
      ) {
        return;
      }

      if (event.deltaX === 0 && event.deltaY === 0) return;

      scrollContainer.scrollBy({
        left: event.deltaX,
        top: event.deltaY,
        behavior: "auto",
      });
      event.preventDefault();
    };

    viewerElement.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      viewerElement.removeEventListener("wheel", handleWheel);
    };
  }, [isReady]);

  useEffect(() => {
    const viewerElement = viewerRef.current;
    if (!viewerElement) return;

    canceledRef.current = false;
    const initializeViewer = async () => {
      setIsReady(false);
      setIsInitializingViewer(true);
      setDocumentError(null);
      if (isInitializingRef.current) {
        setIsInitializingViewer(false);
        return;
      }
      isInitializingRef.current = true;
      try {
        if (!isActive) {
          setIsInitializingViewer(false);
          setDocumentError(null);
          setIsReady(false);
          await disposeViewer();
          viewerElement.innerHTML = "";
          return;
        }

        if (!resolvedDocUrl && !shouldLoadBlankDocument) {
          setIsInitializingViewer(false);
          setIsReady(false);
          return;
        }

        // Let the slideover/layout settle before measuring.
        await new Promise((resolve) =>
          requestAnimationFrame(() => {
            resolve(null);
          }),
        );

        if (canceledRef.current) {
          setIsInitializingViewer(false);
          return;
        }

        await disposeViewer();

        viewerElement.innerHTML = "";

        const instance = await WebViewer(
          {
            path: "/lib/webviewer",
            licenseKey,
            enableOfficeEditing: true,
            extension: "docx",
            filename: resolvedFileName,
            ...(resolvedDocUrl ? { initialDoc: resolvedDocUrl } : {}),
          },
          viewerElement,
        );

        viewerInstanceRef.current = instance;
        applyViewerUISettings(instance);
        scheduleTemplateFieldsRibbonButton(instance);

        const documentViewer = instance.Core.documentViewer;
        const handlePagesUpdated = (changes?: {
          contentChanged?: number[];
        }) => {
          if (changes?.contentChanged?.length) {
            schedulePrettyTagsRefresh();
          }
        };
        pagesUpdatedHandlerRef.current = handlePagesUpdated;
        documentViewer.addEventListener("pagesUpdated", handlePagesUpdated);
        const handleDocumentLoaded = () => {
          if (canceledRef.current) return;
          scheduleTemplateFieldsRibbonButton(instance);
          applyDefaultZoom(instance);
          setIsReady(true);
          setIsInitializingViewer(false);
          scheduleViewerResize();
          if (prettyTagsEnabledRef.current) {
            void setPrettyTagsEnabled(true);
          }
          documentViewer.removeEventListener(
            "documentLoaded",
            handleDocumentLoaded,
          );
        };
        const handleDocumentLoadFailed = () => {
          if (canceledRef.current) return;
          setDocumentError("Failed to load document.");
          setIsInitializingViewer(false);
          documentViewer.removeEventListener(
            "documentLoadFailed",
            handleDocumentLoadFailed,
          );
        };

        documentViewer.addEventListener("documentLoaded", handleDocumentLoaded);
        documentViewer.addEventListener(
          "documentLoadFailed",
          handleDocumentLoadFailed,
        );

        if (shouldLoadBlankDocument && !resolvedDocUrl) {
          documentViewer.loadBlankOfficeEditorDocument();
        }
      } catch {
        if (!canceledRef.current) {
          setDocumentError("Failed to load document.");
          setIsInitializingViewer(false);
        }
      } finally {
        isInitializingRef.current = false;
      }
    };

    void initializeViewer();

    return () => {
      canceledRef.current = true;
    };
  }, [
    applyViewerUISettings,
    scheduleTemplateFieldsRibbonButton,
    applyDefaultZoom,
    disposeViewer,
    isActive,
    licenseKey,
    resolvedDocUrl,
    resolvedFileName,
    schedulePrettyTagsRefresh,
    scheduleViewerResize,
    setPrettyTagsEnabled,
    shouldLoadBlankDocument,
  ]);

  useEffect(() => {
    return () => {
      void disposeViewer();
      resizeTimeoutsRef.current.forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
      resizeTimeoutsRef.current = [];
      templateFieldButtonTimeoutsRef.current.forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
      templateFieldButtonTimeoutsRef.current = [];
      templateFieldsToolbarObserverRef.current?.disconnect();
      templateFieldsToolbarObserverRef.current = null;
      isInitializingRef.current = false;
    };
  }, [disposeViewer]);

  useEffect(() => {
    if (!viewerInstanceRef.current) return;
    viewerInstanceRef.current.UI.setTheme(resolvedTheme);
  }, [isReady, resolvedTheme]);

  useEffect(() => {
    if (!isReady) return;
    scheduleViewerResize();
  }, [isReady, isTemplateFieldsPanelOpen, scheduleViewerResize]);

  const exportDocument = useCallback(async () => {
    if (!viewerInstanceRef.current) return null;
    const documentViewer = viewerInstanceRef.current.Core.documentViewer;
    const fileData = await documentViewer
      .getDocument()
      .getFileData({ downloadType: "office" });
    return new Blob([fileData], { type: DOCX_MIME_TYPE });
  }, []);

  const handleSave = useCallback(async () => {
    if (!onSave) return;
    setIsSaving(true);
    try {
      const blob = await exportDocument();
      if (!blob) return;
      await onSave({ blob, fileName: resolvedFileName });
    } finally {
      setIsSaving(false);
    }
  }, [exportDocument, onSave, resolvedFileName]);

  useImperativeHandle(
    ref,
    () => ({
      exportDocument,
      insertTextAtCursor,
      setPrettyTagsEnabled,
      isReady,
      isLoading: isLoadingFileUrl || isInitializingViewer,
    }),
    [
      exportDocument,
      insertTextAtCursor,
      setPrettyTagsEnabled,
      isLoadingFileUrl,
      isInitializingViewer,
      isReady,
    ],
  );

  useEffect(() => {
    if (!isActive) return;
    onReadyChange?.(isReady);
  }, [isActive, isReady, onReadyChange]);

  const isLoadingDocument = isLoadingFileUrl || isInitializingViewer;
  const hasDocumentError = Boolean(
    documentError || isErrorFileUrl || !hasDocumentSource,
  );

  return (
    <div className={cn("flex min-h-0 w-full flex-1 flex-col gap-3", className)}>
      {showHeader && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="text-primary h-5 w-5" />
            <Typography variant="subHeading">Document editor</Typography>
          </div>
          <div className="flex items-center gap-2">
            {headerActions}
            {onSave && showSaveButton && (
              <Button
                type="button"
                size="sm"
                variant="light-primary"
                onClick={() => {
                  void handleSave();
                }}
                disabled={!canSave || isSaving || !isReady || isLoadingDocument}
              >
                {isSaving ? <LoadingSpinner /> : "Save"}
              </Button>
            )}
          </div>
        </div>
      )}
      <div className="bg-card relative min-h-0 w-full flex-1 overflow-hidden rounded-lg border">
        {isLoadingDocument && (
          <div className="text-muted-foreground absolute inset-0 grid place-items-center text-sm">
            Loading document...
          </div>
        )}
        {hasDocumentError && !isLoadingDocument && (
          <div className="text-destructive absolute inset-0 grid place-items-center text-sm">
            {documentError ??
              (hasDocumentSource
                ? "Failed to load document."
                : "No document available.")}
          </div>
        )}
        <div className="flex h-full min-h-0 w-full">
          <div className="min-h-0 min-w-0 flex-1 overflow-x-auto overflow-y-hidden">
            <div
              ref={viewerRef}
              className={cn(
                "h-full w-full min-w-[960px]",
                isLoadingDocument && "opacity-50",
              )}
            />
          </div>
          {isTemplateFieldsPanelOpen && (
            <div
              className={cn(
                TEMPLATE_FIELDS_PANEL_WIDTH_CLASS,
                "bg-background border-border flex h-full shrink-0 flex-col border-l",
              )}
            >
              <div className="border-border flex items-start justify-between gap-3 border-b px-4 py-3">
                <div className="min-w-0">
                  <Typography variant="subHeading">Template fields</Typography>
                  <Typography className="text-muted-foreground text-xs">
                    Insert tags while keeping the document visible.
                  </Typography>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => {
                      setIsTemplateFieldsPanelOpen(false);
                    }}
                  >
                    <X className="h-4 w-4" />
                    <span className="sr-only">Close template fields panel</span>
                  </Button>
                </div>
              </div>
              <div className="min-h-0 flex-1">
                <TemplateFieldsPanelContent
                  onInsertAtCursor={insertTextAtCursor}
                  bodyClassName="h-full max-h-none"
                  cardGridClassName="grid-cols-1 gap-4 sm:grid-cols-1 lg:grid-cols-1"
                  controlsClassName="gap-3 sm:flex-col sm:items-stretch sm:justify-start"
                  toolbarClassName="items-start"
                  searchInputClassName="w-full lg:w-full"
                  collapseButtonClassName="self-end"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

DocxEditor.displayName = "DocxEditor";

/** Programmatically opens the template-fields popover (ref on its trigger) from the in-editor Apryse control. */
export function useDocxTemplateFieldActions(
  editorRef: RefObject<DocxEditorHandle | null>,
) {
  const templateFieldsTriggerRef = useRef<HTMLButtonElement | null>(null);
  const handleInsertAtCursor = useCallback(
    async (value: string) => {
      if (!editorRef.current?.insertTextAtCursor) return false;
      return editorRef.current.insertTextAtCursor(value);
    },
    [editorRef],
  );
  const handleOpenTemplateFields = useCallback(() => {
    templateFieldsTriggerRef.current?.click();
  }, []);
  return {
    templateFieldsTriggerRef,
    handleInsertAtCursor,
    handleOpenTemplateFields,
  };
}
